import {
  useContractsGetAvailableWorkflowsSuspense,
  useContractsCreateContractTracking,
  getContractsGetContractTrackingWorkflowStepsQueryKey,
  useContractsStartSigning,
  useContractsUploadContract,
} from "@/orval/gen"
import { getDateString } from "@/routes/app/-components/date-time"
import { useInvalidate } from "@/typed-client/hooks/use-invalidate"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { CancelButton, SubmitButton } from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import {
  ModalProps,
  Modal,
  Stack,
  Radio,
  Textarea,
  LoadingOverlay,
  Button,
  Text,
  Group,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

interface Props extends ModalProps {
  contractId: number
  defaultStatusId?: number
  documentAvailable?: boolean
}
export function ApproveContractModal({
  contractId,
  defaultStatusId,
  documentAvailable,
  ...modalProps
}: Props) {
  const { data: workflows, queryKey } =
    useContractsGetAvailableWorkflowsSuspense({
      contractId,
    })

  const workflowStepsQueryKey =
    getContractsGetContractTrackingWorkflowStepsQueryKey({ contractId })

  const queryClient = useQueryClient()

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      approvalStatusId:
        defaultStatusId ??
        workflows.at(0)?.contractApprovalStatus?.contractApprovalStatusId,
      note: undefined,
      trackingDate: getDateString(),
    },
  })

  React.useEffect(() => {
    if (
      workflows.some(
        (w) =>
          w.contractApprovalStatus.contractApprovalStatusId ===
          form.getValues().approvalStatusId
      )
    ) {
      return
    }
    form.setFieldValue(
      "approvalStatusId",
      workflows.at(0)?.contractApprovalStatus?.contractApprovalStatusId
    )
  }, [workflows])

  React.useEffect(() => {
    if (defaultStatusId) {
      form.setFieldValue("approvalStatusId", defaultStatusId)
    }
  }, [defaultStatusId])

  const invalidate = useInvalidate()

  const invalidateContractDetails = React.useCallback(() => {
    invalidate("/api/Contracts/GetContractDetails", { id: contractId })
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: workflowStepsQueryKey })
  }, [queryClient, queryKey, workflowStepsQueryKey, invalidate, contractId])

  const { mutate: createTracking, isPending: createTrackingIsPending } =
    useContractsCreateContractTracking({
      mutation: {
        onError: showErrorNotification,
        onSuccess: () => {
          invalidateContractDetails()
          modalProps.onClose()
          form.initialized = false
        },
      },
    })

  const { mutate: addSigner, isPending: addSignerIsPending } =
    useContractsStartSigning({
      mutation: {
        onError: showErrorNotification,
        onSuccess: (url) => {
          console.log(url)
          location.href = url
        },
      },
    })

  const selectedWorkflow = workflows.find(
    (w) =>
      w.contractApprovalStatus.contractApprovalStatusId ===
      form.getValues().approvalStatusId
  )

  const isSubmitting = createTrackingIsPending || addSignerIsPending

  const uploadRequired =
    selectedWorkflow?.signatureType && !selectedWorkflow?.envelopeId

  const documentRequired = selectedWorkflow?.documentType && !documentAvailable

  const { mutateAsync: uploadContract, isPending: uploadContractIsPending } =
    useContractsUploadContract({
      mutation: {
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
      },
    })

  const handleUpload = async () => {
    await uploadContract({ data: contractId })
  }

  const handleSubmit = (formValues: typeof form.values) => {
    if (!formValues.approvalStatusId) return
    const workflow = workflows.find(
      (w) =>
        w.contractApprovalStatus.contractApprovalStatusId ===
        formValues.approvalStatusId
    )
    if (!workflow) return

    if (workflow.signatureType === "Internal") {
      const { envelopeId } = workflow
      if (!envelopeId) return
      addSigner({
        data: { envelopeId, contractId, workflowId: workflow.workflowId },
      })
    } else {
      createTracking({
        data: {
          ...formValues,
          contractId,
          approvalStatusId: formValues.approvalStatusId,
        },
      })
    }
  }

  const { t } = useTranslation("app_server")

  const radios = React.useMemo(
    () =>
      workflows.map((status) => {
        const { contractApprovalStatusId: statusId, name } =
          status.contractApprovalStatus
        return (
          <Radio
            my={"xs"}
            key={statusId}
            value={statusId.toString()}
            label={name}
            checked={form.getValues().approvalStatusId === statusId}
            onChange={() => form.setFieldValue("approvalStatusId", statusId)}
          />
        )
      }),
    [workflows, form]
  )

  return (
    <Modal closeOnEscape title={t("SetTrackingStatus")} {...modalProps}>
      <LoadingOverlay visible={isSubmitting || uploadContractIsPending} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Radio.Group
            my={"xs"}
            value={form.getValues().approvalStatusId?.toString()}
            label={t("Status")}
          >
            {radios}
          </Radio.Group>
          <DateInput
            valueFormat="L"
            maxDate={new Date()}
            key={form.key("trackingDate")}
            {...form.getInputProps("trackingDate")}
          />
          <Textarea
            label={t("Note")}
            maxLength={250}
            key={form.key("note")}
            {...form.getInputProps("note")}
          />
          {uploadRequired && (
            <Group wrap="nowrap">
              <Text c="red">{t("ContractApproval_UploadRequired")}</Text>
              <Button w="fit-content" onClick={handleUpload}>
                {t("Upload")}
              </Button>
            </Group>
          )}
          {documentRequired && (
            <Text c="red">{t("ContractApproval_DocumentRequired")}</Text>
          )}
          <FormButtonGroup>
            <CancelButton onClick={modalProps.onClose} />
            <SubmitButton disabled={!!uploadRequired || !!documentRequired}>
              {selectedWorkflow?.signatureType === "Internal"
                ? t("Sign")
                : t("Confirm")}
            </SubmitButton>
          </FormButtonGroup>
        </Stack>
      </form>
    </Modal>
  )
}
